import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PageBackButton from '../../components/PageBackButton';
import { classObservationsAPI } from '../../services/api';
import { buildBatchSessionPath } from '../../utils/teacherSessionNavigation';

const OUTCOME_LABEL = {
  all_good: 'All good',
  needs_attention: 'Needs attention',
  issue_found: 'Issue found',
  follow_up: 'Follow up',
};

const SENTIMENT_LABEL = {
  positive: 'Positive',
  neutral: 'Neutral',
  negative: 'Negative',
};

function teacherBits(row) {
  return [
    row.primaryTeacherName ? `Primary ${row.primaryTeacherName}` : '',
    row.secondaryTeacherName ? `Secondary ${row.secondaryTeacherName}` : '',
    row.substituteTeacherName ? `Substitute ${row.substituteTeacherName}` : '',
  ].filter(Boolean).join(' · ');
}

export default function ClassObservations() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);

  const listQuery = useQuery(['class-observations', applied, page], async () => {
    const res = await classObservationsAPI.list({ q: applied || undefined, page, limit: 30 });
    return res.data;
  });

  const rows = listQuery.data?.data || [];
  const pagination = listQuery.data?.pagination;

  return (
    <Box>
      <PageBackButton />
      <Typography variant="h5" sx={{ mb: 0.5 }}>Class observations</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Observations written by teaching supervisors and admins after attending a class. Open a batch class to add one.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search batch, teacher, or notes"
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              setApplied(search.trim());
              setPage(1);
            }
          }}
        />
        <Button
          variant="contained"
          onClick={() => {
            setApplied(search.trim());
            setPage(1);
          }}
        >
          Search
        </Button>
      </Stack>
      {listQuery.isLoading ? <CircularProgress /> : null}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Class</TableCell>
              <TableCell>Teachers</TableCell>
              <TableCell>Observation</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const path = buildBatchSessionPath(row.batchId, row.sessionId, { sessionDate: row.sessionDate });
              return (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{row.batchName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.sessionDate}{row.startTime ? ` · ${row.startTime}` : ''}{row.endTime ? `–${row.endTime}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{teacherBits(row) || 'No teacher recorded'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                      {row.outcome ? <Chip size="small" label={OUTCOME_LABEL[row.outcome] || row.outcome} /> : null}
                      {row.sentiment ? <Chip size="small" variant="outlined" label={SENTIMENT_LABEL[row.sentiment] || row.sentiment} /> : null}
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.notes}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {row.createdByName || 'Unknown'}
                      {row.createdAt ? ` · ${new Date(row.createdAt).toLocaleString()}` : ''}
                      {row.nextFollowUpDate ? ` · Follow-up ${row.nextFollowUpDate}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {path ? (
                      <Button size="small" onClick={() => navigate(path)}>Open class</Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
            {!listQuery.isLoading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">No class observations yet.</Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination?.pages > 1 ? (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination count={pagination.pages} page={pagination.page || page} onChange={(_event, next) => setPage(next)} />
        </Stack>
      ) : null}
    </Box>
  );
}
